import {
  AssessmentRecord,
  FileLinks,
  GradingReport,
  Role,
  StudentDiagnosticReport,
  User,
} from '../types';
import { fromLegacyGradingReport } from './reportAdapters';

const DEFAULT_USERS: User[] = [
  {
    id: 't1',
    name: 'Mrs. Priti Dhandrut',
    role: 'teacher',
    avatar: '👩‍🏫',
    username: 'Priti Dhandrut',
    password: 'Priti@123',
  },
  {
    id: 's1',
    name: 'Alex Johnson',
    role: 'student',
    avatar: '👦',
    username: 'alex',
    password: 'password',
  },
  {
    id: 's2',
    name: 'Sam Smith',
    role: 'student',
    avatar: '👧',
    username: 'sam',
    password: 'password',
  },
];

const MOCK_REPORT = fromLegacyGradingReport({
  studentName: 'Alex Johnson',
  gradeLevel: '6',
  subject: 'Science',
  totalScore: 85,
  maxTotalScore: 100,
  percentage: 85,
  overallFeedback: 'Great job understanding the water cycle!',
  strengths: ['Diagram interpretation', 'Vocabulary'],
  areasForImprovement: ['Detailed explanations'],
  questionAnalysis: [],
  pagesUsed: [1],
  segregationReasoning: 'Single page uploaded.',
});

const MOCK_ASSESSMENTS: AssessmentRecord[] = [
  {
    id: 'a1',
    studentId: 'Alex Johnson',
    studentName: 'Alex Johnson',
    subject: 'Science',
    gradeLevel: '6',
    score: 85,
    maxScore: 100,
    dateGraded: new Date(Date.now() - 86400000).toISOString(),
    fileLinks: {
      studentSheet: 'https://drive.google.com/file/d/mock-student-sheet',
      questionPaper: 'https://drive.google.com/file/d/mock-question-paper',
      answerKey: 'https://drive.google.com/file/d/mock-answer-key',
      rubric: 'https://drive.google.com/file/d/mock-rubric',
    },
    report: MOCK_REPORT,
  },
];

const isLegacyReport = (report: StudentDiagnosticReport | GradingReport): report is GradingReport =>
  'questionAnalysis' in report;

const migrateAssessment = (
  assessment: AssessmentRecord | (Omit<AssessmentRecord, 'report'> & { report: GradingReport }),
): AssessmentRecord => {
  const report = isLegacyReport(assessment.report)
    ? fromLegacyGradingReport(assessment.report)
    : assessment.report;

  return {
    ...assessment,
    score: report.totalScore,
    maxScore: report.maxTotalScore,
    studentName: report.studentName,
    subject: report.subject,
    gradeLevel: report.gradeLevel,
    report,
  };
};

class MockDatabase {
  private users: User[];
  private assessments: AssessmentRecord[];

  constructor() {
    if (typeof window !== 'undefined' && window.localStorage) {
      const storedUsers = localStorage.getItem('edugrade_users');
      this.users = storedUsers ? JSON.parse(storedUsers) : [...DEFAULT_USERS];

      const storedAssessments = localStorage.getItem('edugrade_assessments');
      const parsedAssessments = storedAssessments
        ? (JSON.parse(storedAssessments) as Array<
            AssessmentRecord | (Omit<AssessmentRecord, 'report'> & { report: GradingReport })
          >)
        : [...MOCK_ASSESSMENTS];
      this.assessments = parsedAssessments.map(migrateAssessment);
      if (storedAssessments) {
        this.persistAssessments();
      }
    } else {
      this.users = [...DEFAULT_USERS];
      this.assessments = [...MOCK_ASSESSMENTS];
    }
  }

  private persistUsers() {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('edugrade_users', JSON.stringify(this.users));
    }
  }

  private persistAssessments() {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('edugrade_assessments', JSON.stringify(this.assessments));
    }
  }

  getUsers(): User[] {
    return this.users;
  }

  authenticate(username: string, password: string): User | undefined {
    return this.users.find((user) => user.username === username && user.password === password);
  }

  loginOrRegisterGoogleUser(email: string, name: string, picture: string, role: Role): User {
    let user = this.users.find((existingUser) => existingUser.username === email);

    if (!user) {
      user = {
        id: Math.random().toString(36).substr(2, 9),
        name,
        username: email,
        role,
        avatar: picture,
        password: '',
      };
      this.users.push(user);
      this.persistUsers();
    } else if (picture && user.avatar !== picture) {
      user.avatar = picture;
      this.persistUsers();
    }

    return user;
  }

  registerUser(name: string, username: string, password: string, role: Role): User {
    if (this.users.some((user) => user.username === username)) {
      throw new Error('Username already taken');
    }

    const newUser: User = {
      id: Math.random().toString(36).substr(2, 9),
      name,
      username,
      password,
      role,
      avatar: role === 'teacher' ? '👩‍🏫' : '🧑‍🎓',
    };

    this.users.push(newUser);
    this.persistUsers();
    return newUser;
  }

  getAssessments(): AssessmentRecord[] {
    return this.assessments;
  }

  getAssessmentsByStudent(studentName: string): AssessmentRecord[] {
    return this.assessments.filter(
      (assessment) =>
        assessment.studentName.toLowerCase().includes(studentName.toLowerCase()) ||
        studentName.toLowerCase().includes(assessment.studentName.toLowerCase()),
    );
  }

  saveAssessment(report: StudentDiagnosticReport, fileLinks?: FileLinks): void {
    const newRecord: AssessmentRecord = {
      id: Math.random().toString(36).substr(2, 9),
      studentId: report.studentName,
      studentName: report.studentName,
      subject: report.subject,
      gradeLevel: report.gradeLevel,
      score: report.totalScore,
      maxScore: report.maxTotalScore,
      dateGraded: new Date().toISOString(),
      report,
      fileLinks,
    };
    this.assessments.unshift(newRecord);
    this.persistAssessments();
  }

  getClassAnalytics() {
    if (this.assessments.length === 0) return { average: 0, count: 0 };

    const total = this.assessments.reduce((sum, assessment) => {
      if (assessment.maxScore === 0) return sum;
      return sum + (assessment.score / assessment.maxScore) * 100;
    }, 0);

    return {
      average: Math.round(total / this.assessments.length),
      count: this.assessments.length,
    };
  }
}

export const db = new MockDatabase();
